import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import { CerereTypeService } from '../cerere-type.service';
import { AuthService } from 'src/app/services/auth.service';
import { CerereTypeAddPayload } from '../cerere-type.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { Observable, map, finalize, Subject, of, switchMap, distinctUntilChanged, takeUntil, tap, catchError } from 'rxjs';
import { DirectiiService } from '../../directii/directii.service';
import { CompartimenteService } from '../../compartimente/compartimente.service';
import { CerereStatusService } from '../../cerere-status/cerere-status.service';
import { CererePriorityService } from '../../cerere-priority/cerere-priority.service';
import { UsersService } from '../../users-settings/users.service';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';
// import { notDefaultValidator } from 'src/app/shared/validators/not-default';

@Component({
    selector: 'app-add-cerere-type',
    templateUrl: './add-cerere-type.component.html',
    styleUrls: ['./add-cerere-type.component.scss'],
})
export class AddCerereTypeComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        slug: new FormControl('', [Validators.required]),
        priority_id: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
        status_id: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
        directie_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        compartiment_id: new FormControl(-1),
        user_id: new FormControl(-1, []),
        observations: new FormControl(''),
        indoc_name: new FormControl(''),
        indoc_id: new FormControl(''),
    });

    directiiActive: Generic[] = [];
    compartimenteActive!: Generic[];
    usersActive!: Generic[];
    statusesActive$!: Observable<Generic[]>;
    prioritiesActive$!: Observable<Generic[]>;

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    constructor(
        private dialogRef: MatDialogRef<AddCerereTypeComponent>,
        private cerereTypeService: CerereTypeService,
        public directiiService: DirectiiService,
        public compartimenteService: CompartimenteService,
        public cerereStatusService: CerereStatusService,
        public cererePriorityService: CererePriorityService,
        public usersService: UsersService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private fb: FormBuilder,
    ) {
        this.processToken();
    }

    ngOnInit() {
        this.loadDirectiiActive();
        this.loadStatususesActive();
        this.loadPrioritiesActive();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.cerereTypeService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            compartiment_id,
            user_id,
            observations,
            indoc_name,
            indoc_id,
        } = this.form.value as CerereTypeAddPayload;
        const newCerereType: CerereTypeAddPayload = {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            user_id,
            compartiment_id,
            observations,
            indoc_name,
            indoc_id,
        };

        this.cerereTypeService
            .addRequestType(newCerereType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.directiiService
            .getDirectiiActive()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.directiiActive = res;
                    this.filteredDirectii = res;
                },
                error: (err) => {
                    console.error('Error in get directii active:', err);
                }
            });
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['user_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive()
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.compartimenteService.getCompartimenteByDirectieId(directieId)
            : this.compartimenteService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        });
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['user_id'].patchValue(-1);

        if (compartimentId) {
            this.loadUsersActive();
        }
    }

    loadUsersActive() {
        this.isLoading = true;
        let formValues = { ...this.form.value };
        let formValuesForm: FormGroup = this.fb.group(formValues);

        if (formValuesForm.get('compartiment_id')?.value === -1) {
            formValuesForm.get('compartiment_id')?.setValue(null);
        }

        this.usersService
            .getActiveUsersByOrganigrama(formValuesForm.value)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            })
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.form.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadStatususesActive() {
        this.isLoading = true;
        this.statusesActive$ = this.cerereStatusService
            .getActiveStatus()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }

    loadPrioritiesActive() {
        this.isLoading = true;
        this.prioritiesActive$ = this.cererePriorityService
            .getActiveCererePriorities()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }
}
